import { InvalidateQueryFilters, useQueryClient } from '@tanstack/react-query'
import { firebaseApp } from 'config/firebaseConfig'
import {
    createUserWithEmailAndPassword,
    getAuth,
    onIdTokenChanged,
    signInWithEmailAndPassword,
    signOut,
    User as FirebaseUser,
    UserCredential,
} from 'firebase/auth'
import { useToken } from 'hooks/token'
import { GUEST_TOKEN_KEY } from 'hooks/token/useToken'
import React, { createContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { clearAllSeatAndPaymentStorage } from 'utils/sessionStorage'

interface User extends FirebaseUser {}

export interface AuthContextType {
    currentUser: User | null
    signIn: (email: string, password: string) => Promise<UserCredential>
    createUser: (email: string, password: string) => Promise<UserCredential>
    signOut: () => Promise<void>
    isTokenExpired: () => Promise<boolean>
    getFreshToken: () => Promise<string>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const { createGuestToken, removeAuthorizedToken, setAuthorizedToken } = useToken()
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    const auth = getAuth(firebaseApp)

    useEffect(() => {
        const unsubscribe = onIdTokenChanged(auth, async (user) => {
            if (user) {
                const token = await user.getIdToken(true)
                setAuthorizedToken(token)
                setCurrentUser(user)
            } else {
                setCurrentUser(null)
                removeAuthorizedToken()
                await createGuestToken()
            }

            // Set only once
            if (typeof window !== 'undefined' && !(window as any).firebaseAuthResolved) {
                ;(window as any).firebaseAuthResolved = true
            }

            setLoading(false)
        })

        return () => unsubscribe()
    }, [auth, setAuthorizedToken, removeAuthorizedToken, createGuestToken])

    useEffect(() => {
        const interval = setInterval(
            async () => {
                if (currentUser) {
                    try {
                        const token = await currentUser.getIdToken(true)
                        setAuthorizedToken(token)
                    } catch (err) {
                        console.error('Failed to refresh token:', err)
                    }
                }
            },
            (45 + Math.floor(Math.random() * 10)) * 60 * 1000,
        )

        return () => clearInterval(interval)
    }, [currentUser, setAuthorizedToken])

    const signIn = async (email: string, password: string) => {
        try {
            // Clear seat and payment storage when switching from guest to authenticated
            clearAllSeatAndPaymentStorage()

            const response = await signInWithEmailAndPassword(auth, email, password)
            const token = await response.user.getIdToken()
            setAuthorizedToken(token)
            queryClient.invalidateQueries(['currentAccount'] as InvalidateQueryFilters)
            return response
        } catch (error) {
            console.error('Error signing in:', error)
            throw error
        }
    }

    const createUser = async (email: string, password: string) => {
        try {
            return await createUserWithEmailAndPassword(auth, email, password)
        } catch (error) {
            console.error('Error creating user:', error)
            throw error
        }
    }

    const signOutUser = async () => {
        try {
            setCurrentUser(null)
            await signOut(auth)
            removeAuthorizedToken()
            localStorage.removeItem(GUEST_TOKEN_KEY)

            // Clear seat and payment storage when signing out
            clearAllSeatAndPaymentStorage()

            await createGuestToken()
            queryClient.invalidateQueries(['currentAccount'] as InvalidateQueryFilters)
            navigate('/')
            window.location.reload()
        } catch (error) {
            console.error('Sign-out failed:', error)
        }
    }

    const isTokenExpired = async (): Promise<boolean> => {
        if (!currentUser) return true
        try {
            const tokenResult = await currentUser.getIdTokenResult()
            const expirationTime = new Date(tokenResult.expirationTime).getTime()
            return Date.now() >= expirationTime
        } catch (error) {
            console.error('Error checking token expiration:', error)
            return true
        }
    }

    const getFreshToken = async (): Promise<string> => {
        if (!currentUser) throw new Error('User not authenticated')
        return await currentUser.getIdToken(true)
    }

    const contextValue: AuthContextType = {
        currentUser,
        signIn,
        createUser,
        signOut: signOutUser,
        isTokenExpired,
        getFreshToken,
    }

    return <AuthContext.Provider value={contextValue}>{!loading && children}</AuthContext.Provider>
}
